import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "chips", "hiddenInputs", "clearButton"]
  static values = {
    searchUrl: String,
    emptyText: { type: String, default: "No se encontraron términos de búsqueda" }
  }

  connect() {
    this.timeout = null
    this.updateClearButton()
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout)
  }

  search() {
    if (this.timeout) clearTimeout(this.timeout)

    this.timeout = setTimeout(() => this.fetchResults(), 200)
  }

  async fetchResults() {
    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      this.clearResults()
      return
    }

    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("q", query)

    const response = await fetch(url, { headers: { Accept: "application/json" } })
    const searchTerms = await response.json()
    this.renderResults(Array.isArray(searchTerms) ? searchTerms : [])
  }

  add(event) {
    event.preventDefault()

    const query = event.currentTarget.dataset.query
    if (!query || this.selectedQueries().includes(query)) return

    this.chipsTarget.querySelector("[data-empty-state]")?.remove()
    this.hiddenInputsTarget.insertAdjacentHTML("beforeend", this.hiddenInput(query))
    this.chipsTarget.insertAdjacentHTML("beforeend", this.chip(query))
    this.inputTarget.value = ""
    this.clearResults()
    this.updateClearButton()
  }

  remove(event) {
    event.preventDefault()

    const query = event.currentTarget.dataset.query
    this.element.querySelectorAll(`[data-search-query="${CSS.escape(query)}"]`).forEach((element) => element.remove())
    this.updateClearButton()
  }

  clearAll(event) {
    event.preventDefault()

    const url = new URL(window.location.href)
    this.deleteParamVariants(url, "search_queries")
    url.searchParams.set("tab", "user_search")

    window.location.href = url.toString()
  }

  deleteParamVariants(url, baseName) {
    url.searchParams.delete(baseName)
    url.searchParams.delete(`${baseName}[]`)
  }

  updateClearButton() {
    if (!this.hasClearButtonTarget) return

    this.clearButtonTarget.classList.toggle("hidden", this.selectedQueries().length === 0)
  }

  renderResults(searchTerms) {
    if (searchTerms.length === 0) {
      this.resultsTarget.innerHTML = `<div class="px-3 py-2 text-sm text-gray-500">${this.escapeHtml(this.emptyTextValue)}</div>`
      this.resultsTarget.classList.remove("hidden")
      return
    }

    const selected = this.selectedQueries()
    this.resultsTarget.innerHTML = searchTerms.map((searchTerm) => {
      const query = searchTerm.query
      const disabled = selected.includes(query)
      const classes = disabled ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-brand-primary-subtle"

      return `
        <button type="button"
                class="block w-full px-3 py-2 text-left text-sm ${classes}"
                data-action="search-terms-selector#add"
                data-query="${this.escapeHtml(query)}"
                ${disabled ? "disabled" : ""}>
          ${this.escapeHtml(query)}
        </button>
      `
    }).join("")
    this.resultsTarget.classList.remove("hidden")
  }

  clearResults() {
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("hidden")
  }

  selectedQueries() {
    return Array.from(this.hiddenInputsTarget.querySelectorAll('input[name="search_queries[]"]')).map((input) => input.value)
  }

  hiddenInput(query) {
    return `<input type="hidden" name="search_queries[]" value="${this.escapeHtml(query)}" data-search-query="${this.escapeHtml(query)}">`
  }

  chip(query) {
    return `
      <button type="button"
              class="rounded-full bg-brand-primary-subtle px-3 py-1 text-xs font-medium text-brand-primary hover:bg-brand-primary-subtle"
              data-action="search-terms-selector#remove"
              data-query="${this.escapeHtml(query)}"
              data-search-query="${this.escapeHtml(query)}">
        ${this.escapeHtml(query)} ×
      </button>
    `
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
